import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import { getMathConceptsForStudent } from '../services/contentService';
import { isDemoMode } from '../services/supabase';
import { logActivity, checkAndAwardAchievements } from '../services/activityService';
import { ttsSupported } from '../utils/voiceUtils';
import './Activity.css';
import './MathConceptsActivity.css';

const STAGE_INFO = {
  concrete:         { icon: 'fas fa-hand-paper', label: 'Concrete',         desc: 'Touch & Count',        color: '#e67e22' },
  representational: { icon: 'fas fa-circle',     label: 'Representational', desc: 'Picture & Count',      color: '#3498db' },
  abstract:         { icon: 'fas fa-hashtag',    label: 'Abstract',         desc: 'Numbers & Symbols',    color: '#9b59b6' },
};

// ─── Dot Pattern visual (representational stage) ─────────────────
const DotPattern = ({ count }) => (
  <div className={`dot-pattern dots-${count}`} aria-label={`${count} dots`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="dot" />
    ))}
  </div>
);

// ─── Component ────────────────────────────────────────────────────
const MathConceptsActivity = ({ onComplete }) => {

  const { currentUser } = useUser();

  const [concepts, setConcepts]           = useState([]);
  const [loadingConcepts, setLoadingConcepts] = useState(true);
  const [currentIndex, setCurrentIndex]   = useState(0);
  const [completed, setCompleted]         = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [shakeChoice, setShakeChoice]     = useState(null);
  const [answered, setAnswered]           = useState(false);
  const [autoCorrect, setAutoCorrect]     = useState(false);
  const [isSpeaking, setIsSpeaking]       = useState(false);
  const [speechRate, setSpeechRate]       = useState(1.0);

  const speechRateRef    = useRef(speechRate);
  speechRateRef.current  = speechRate;
  const hasNavigatedRef  = useRef(false);
  const autoAdvanceTimer = useRef(null);
  const nextConceptRef   = useRef(null);

  const current = concepts[currentIndex];

  useEffect(() => {
    return () => { if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current); };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    setLoadingConcepts(true);
    getMathConceptsForStudent(currentUser.id).then(({ data }) => {
      setConcepts(data || []);
      setLoadingConcepts(false);
    });
  }, [currentUser]);

  // Auto-play question when navigating (not on initial load)
  useEffect(() => {
    if (!hasNavigatedRef.current || !ttsSupported) return;
    const timer = setTimeout(() => playQuestion(), 400);
    return () => clearTimeout(timer);
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── TTS ──────────────────────────────────────────────────────────
  const playQuestion = () => {
    if (!ttsSupported || !current) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(current.question);
    utterance.rate    = speechRateRef.current;
    utterance.lang    = 'en-US';
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend   = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  // ── Errorless Learning: reduce choices after 2 wrong ─────────────
  const getDisplayChoices = () => {
    if (wrongAttempts >= 2 && !answered) {
      const wrongs = current.choices.filter(c => c !== current.answer);
      return [current.answer, wrongs[0]].sort((a, b) => a - b);
    }
    return current.choices;
  };

  // ── Answer handler ────────────────────────────────────────────────
  const handleAnswer = (choice) => {
    if (answered) return;

    if (choice === current.answer) {
      setAnswered(true);
      if (currentUser && !isDemoMode) {
        logActivity(currentUser.id, 'math_concepts', current.id, true, wrongAttempts + 1);
      }
      if (ttsSupported) {
        window.speechSynthesis.cancel();
        const praise = new SpeechSynthesisUtterance('Great job!');
        praise.rate = speechRateRef.current;
        praise.lang = 'en-US';
        window.speechSynthesis.speak(praise);
      }
      autoAdvanceTimer.current = setTimeout(() => nextConceptRef.current(), 1800);
    } else {
      // Silent correction — no negative buzz, just a shake
      const next = wrongAttempts + 1;
      setWrongAttempts(next);
      setShakeChoice(choice);
      setTimeout(() => setShakeChoice(null), 500);

      // After 3 wrong: reveal correct answer and auto-advance
      if (next >= 3) {
        setAutoCorrect(true);
        setAnswered(true);
        if (currentUser && !isDemoMode) {
          logActivity(currentUser.id, 'math_concepts', current.id, false, 3);
        }
        autoAdvanceTimer.current = setTimeout(() => nextConceptRef.current(), 2400);
      }
    }
  };

  // ── Next concept ──────────────────────────────────────────────────
  const nextConcept = () => {
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
    window.speechSynthesis.cancel();
    if (currentIndex + 1 >= concepts.length) {
      setCompleted(true);
      if (currentUser && !isDemoMode) checkAndAwardAchievements(currentUser.id);
      return;
    }
    hasNavigatedRef.current = true;
    setCurrentIndex(prev => prev + 1);
    setWrongAttempts(0);
    setShakeChoice(null);
    setAnswered(false);
    setAutoCorrect(false);
    setIsSpeaking(false);
  };
  nextConceptRef.current = nextConcept;

  if (loadingConcepts) {
    return (
      <div className="activity-container">
        <div className="activity-header math">
          <h2><i className="fas fa-calculator" aria-hidden="true"></i> Math Concepts</h2>
        </div>
        <div className="activity-content" style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading math concepts...</p>
        </div>
      </div>
    );
  }

  if (concepts.length === 0) {
    return (
      <div className="activity-container">
        <div className="activity-header math">
          <h2><i className="fas fa-calculator" aria-hidden="true"></i> Math Concepts</h2>
        </div>
        <div className="activity-content" style={{ textAlign: 'center', padding: '2rem' }}>
          <p>No math concepts assigned yet. Ask your teacher to assign some!</p>
        </div>
      </div>
    );
  }

  // ── Completed screen ──────────────────────────────────────────────
  if (completed) {
    return (
      <div className="activity-container">
        <div className="activity-header math">
          <h2><i className="fas fa-calculator" aria-hidden="true"></i> Math Concepts</h2>
        </div>
        <div className="activity-content" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏆</div>
          <p style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Amazing work!
          </p>
          <p style={{ color: '#555', marginBottom: '2rem' }}>
            You completed all {concepts.length} math concepts — Concrete, Representational, and Abstract!
          </p>
          <button className="btn btn-accent" onClick={onComplete}>
            <i className="fas fa-chart-bar" aria-hidden="true"></i> See My Progress
          </button>
        </div>
      </div>
    );
  }

  const stageInfo      = STAGE_INFO[current.stage];
  const displayChoices = getDisplayChoices();

  return (
    <div className="activity-container">
      <div className="activity-header math">
        <h2><i className="fas fa-calculator" aria-hidden="true"></i> Math Concepts</h2>
        <p>Look, listen, and choose the right answer!</p>
      </div>

      <div className="activity-content">

        {/* ── CRA Stage Badge ── */}
        <div className="math-stage-badge" style={{ background: stageInfo.color }}>
          <i className={stageInfo.icon} aria-hidden="true"></i>
          {' '}{stageInfo.label} Stage
          <span className="math-stage-desc">{stageInfo.desc}</span>
        </div>

        {/* ── Question text ── */}
        <div className="math-question" aria-live="polite">
          {current.question}
        </div>

        {/* ── Visual display (changes by CRA stage) ── */}
        {current.stage === 'concrete' && (
          <div className="math-visual" aria-label={`Visual objects: ${current.visual}`}>
            {current.visual}
          </div>
        )}

        {current.stage === 'representational' && (
          <DotPattern count={current.dots} />
        )}

        {current.stage === 'abstract' && (
          <div className="math-expression" aria-label={current.question}>
            {current.expression}
          </div>
        )}

        {/* ── TTS controls ── */}
        <div className="controls" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
          <button
            className="btn btn-secondary"
            onClick={playQuestion}
            disabled={isSpeaking || !ttsSupported}
            aria-label="Hear the question read aloud"
          >
            <i className={isSpeaking ? 'fas fa-spinner fa-spin' : 'fas fa-volume-up'} aria-hidden="true"></i>
            {isSpeaking ? 'Speaking...' : 'Hear Question'}
          </button>

          {ttsSupported && (
            <div className="tts-speed-control" style={{ margin: 0, flex: '1 1 auto' }}>
              <label htmlFor="math-speech-rate" className="speed-label">
                <i className="fas fa-tachometer-alt" aria-hidden="true"></i>
                Speed: <strong>{speechRate.toFixed(1)}x</strong>
              </label>
              <input
                id="math-speech-rate"
                type="range"
                min="0.5"
                max="2.0"
                step="0.1"
                value={speechRate}
                onChange={e => setSpeechRate(parseFloat(e.target.value))}
                aria-label="Speech speed"
              />
              <span className="speed-hint">Slow  Fast</span>
            </div>
          )}
        </div>

        {/* ── Errorless Learning hint messages ── */}
        {wrongAttempts === 1 && !answered && (
          <div className="math-hint" role="status" aria-live="polite">
            <i className="fas fa-lightbulb" aria-hidden="true"></i> Look carefully — one answer is glowing!
          </div>
        )}
        {wrongAttempts === 2 && !answered && (
          <div className="math-hint" role="status" aria-live="polite">
            <i className="fas fa-star" aria-hidden="true"></i> You got this! I removed some choices to help.
          </div>
        )}
        {autoCorrect && (
          <div className="math-hint success" role="status" aria-live="polite">
            <i className="fas fa-check-circle" aria-hidden="true"></i> Here is the answer — great try! Moving on...
          </div>
        )}
        {answered && !autoCorrect && (
          <div className="math-hint success" role="status" aria-live="polite">
            <i className="fas fa-star" aria-hidden="true"></i> Fantastic! Moving to the next one...
          </div>
        )}

        {/* ── Answer choice buttons ── */}
        <div className="math-choices" role="group" aria-label="Answer choices">
          {displayChoices.map(choice => {
            let cls = 'math-choice-btn';
            if (answered && choice === current.answer)       cls += autoCorrect ? ' auto-correct' : ' correct';
            else if (answered && choice !== current.answer)  cls += ' dimmed';
            else if (!answered && wrongAttempts >= 1 && choice === current.answer) cls += ' prompt-hint';
            if (shakeChoice === choice) cls += ' shake';
            return (
              <button
                key={choice}
                className={cls}
                onClick={() => handleAnswer(choice)}
                disabled={answered}
                aria-label={`Answer ${choice}`}
              >
                {choice}
              </button>
            );
          })}
        </div>

        {/* ── Progress counter ── */}
        <div className="attempts" aria-live="polite">
          Question {currentIndex + 1} of {concepts.length}
          {wrongAttempts > 0 && !answered && ` — Keep trying!`}
        </div>

        {/* ── Skip / Next button ── */}
        <div className="controls">
          <button className="btn btn-accent" onClick={nextConcept}>
            <i className="fas fa-arrow-right" aria-hidden="true"></i> Next
          </button>
        </div>

      </div>
    </div>
  );
};

export default MathConceptsActivity;
